import type { ErrorRouteTelemetry } from '@/models/api/Payload'
import { ErrorType } from '@/pages/Error/CodeErros'

// Error mapping: maps API error codes to ErrorType
const ERROR_MAP: Record<string, ErrorType> = {
  NAO_AUTORIZADO: 'NAO_AUTORIZADO',
  SEM_AUTORIZACAO_PARA_CONSULTAR_FGTS: 'NAO_AUTORIZADO',
  SALDO_INSUFICIENTE: 'SALDO_INSUFICIENTE',
  LIMITE_ANTECIPACAO_EXCEDIDO: 'LIMITE_ANTECIPACAO_EXCEDIDO',
  NAO_ENCONTRADO: 'NAO_ENCONTRADO',
  FORA_DO_HORARIO: 'FORA_DO_HORARIO',
  CREDITO_INDISPONIVEL: 'CREDITO_INDISPONIVEL',
  SERVICO_INDISPONIVEL: 'SERVICO_INDISPONIVEL',
  PROCESSO_ANDAMENTO: 'PROCESSO_ANDAMENTO',
  SIMULACAO_ASSINCRONA: 'SIMULACAO_ASSINCRONA',
  MANUTENCAO_CADASTRAL_NO_FGTS: 'MANUTENCAO_CADASTRAL_NO_FGTS',
  SEM_ADESAO_SAQUE_ANIVERSARIO: 'SEM_ADESAO_SAQUE_ANIVERSARIO',
  SERVICO_TEMPORARIAMENTE_INDISPONIVEL: 'SERVICO_TEMPORARIAMENTE_INDISPONIVEL',
  SERVICO_CEF_INDISPONIVEL: 'SERVICO_CEF_INDISPONIVEL',
  SERVICO_INDISPONIVEL_CAIXA: 'SERVICO_INDISPONIVEL_CAIXA',
  QUANTIDADE_EXERCICIOS_SUPERIOR_MAXIMO_PERMITIDO:
    'QUANTIDADE_EXERCICIOS_SUPERIOR_MAXIMO_PERMITIDO',
  VALOR_RESERVA_MENOR_MINIMO_PERMITIDO: 'VALOR_RESERVA_MENOR_MINIMO_PERMITIDO',
  VALOR_RESERVA_MAIOR_MAXIMO_PERMITIDO: 'VALOR_RESERVA_MAIOR_MAXIMO_PERMITIDO',
  TRABALHADOR_JA_POSSUI_QUANTIDADE_MAXIMA_OPERACOES_RESERVA:
    'TRABALHADOR_JA_POSSUI_QUANTIDADE_MAXIMA_OPERACOES_RESERVA',
  TRABALHADOR_JA_POSSUI_VALOR_MAXIMO_RESERVA: 'TRABALHADOR_JA_POSSUI_VALOR_MAXIMO_RESERVA',
  VALOR_TOTAL_RESERVA_EXERCICIOS_SUPERIOR_MAXIMO: 'VALOR_TOTAL_RESERVA_EXERCICIOS_SUPERIOR_MAXIMO',
  EXERCICIOS_FORA_LIMITE_PERMITIDO: 'EXERCICIOS_FORA_LIMITE_PERMITIDO',
  /** SimulacaoSlice getMensagemErro: extraction / parse failed — not a business code from API */
  __ERROR_EXTRACTION_FAILED__: 'ERRO_RESPOSTA_INVALIDA',
  ERRO_RESPOSTA_INVALIDA: 'ERRO_RESPOSTA_INVALIDA',
}

/** Max length for raw API code / message snippets sent to native telemetry only. */
export const RAW_API_MESSAGE_TELEMETRY_MAX_LENGTH = 120

export function truncateRawApiMessageForTelemetry(message: string): string {
  const s = message.trim()
  if (s.length <= RAW_API_MESSAGE_TELEMETRY_MAX_LENGTH) return s
  return `${s.slice(0, RAW_API_MESSAGE_TELEMETRY_MAX_LENGTH)}…`
}

type ErrorTelemetryExtras = Pick<ErrorRouteTelemetry, 'httpStatus' | 'isResponseExtractionFailed'>

/**
 * HTTP status from the caught error when present (webview bridge JSON envelope, or dev-only HTTP clients).
 * Production uses inter-webview-bridge, not Axios; shapes often expose `httpStatus` or `response.status`.
 */
export function getHttpStatusFromUnknownError(e: unknown): number | undefined {
  if (typeof e === 'string') {
    try {
      const parsed = JSON.parse(e) as { httpStatus?: number }
      const s = parsed?.httpStatus
      return typeof s === 'number' && Number.isFinite(s) ? s : undefined
    } catch {
      return undefined
    }
  }
  if (e !== null && typeof e === 'object') {
    const o = e as { httpStatus?: unknown; response?: { status?: unknown } }
    if (typeof o.httpStatus === 'number' && Number.isFinite(o.httpStatus)) {
      return o.httpStatus
    }
    const fromResponse = o.response?.status
    if (typeof fromResponse === 'number' && Number.isFinite(fromResponse)) {
      return fromResponse
    }
  }
  return undefined
}

/** Builds route state telemetry for the Error page (TAC / analytics). */
export function buildErrorRouteTelemetry(
  apiMessage: string | undefined,
  resolvedType: ErrorType,
  extras?: ErrorTelemetryExtras,
): ErrorRouteTelemetry | undefined {
  const out: ErrorRouteTelemetry = {}

  if (resolvedType === 'ERRO_NAO_CATALOGADO') {
    const trimmed = typeof apiMessage === 'string' ? apiMessage.trim() : ''
    if (trimmed !== '') {
      out.rawApiMessageSnippet = truncateRawApiMessageForTelemetry(trimmed)
    }
  }

  if (extras?.httpStatus !== undefined && Number.isFinite(extras.httpStatus)) {
    out.httpStatus = extras.httpStatus
  }
  if (extras?.isResponseExtractionFailed === true) {
    out.isResponseExtractionFailed = true
  }

  if (
    out.rawApiMessageSnippet === undefined &&
    out.httpStatus === undefined &&
    out.isResponseExtractionFailed === undefined
  ) {
    return undefined
  }
  return out
}

/**
 * Maps API erro.mensagem strings to catalogued ErrorType values.
 * Unknown non-empty codes become ERRO_NAO_CATALOGADO so analytics can segment unmapped responses.
 */
const validarErro = (apiMessage: string | undefined): ErrorType => {
  const trimmed = typeof apiMessage === 'string' ? apiMessage.trim() : ''
  if (trimmed === '') {
    return 'ERROR_ROUTE_FALLBACK'
  }
  const mapped = ERROR_MAP[trimmed]
  if (mapped !== undefined) {
    return mapped
  }
  return 'ERRO_NAO_CATALOGADO'
}

export default validarErro
